import { getOfficialMark } from './shootingMarks';
import { resolveWeaponDisplay } from './conventionResults';

const STAGE_TYPE_DISPLAY = {
    nais: 'NAIS',
    hurtig: 'Hurtig',
    'hurtig-half': 'Hurtig halv',
    standard: 'Standard',
    felt: 'Felt',
    norgesfelt: 'Norgesfelt',
    't-96': 'T-96',
    finpistol: 'VM-Finpistol',
    grovpistol: 'VM-Grovpistol',
    silhuett: 'Silhuettpistol',
    luftpistol: 'Luftpistol 10m',
    skarpskytter: 'Skarpskyttermerke',
    tidsoving: 'Tidsøvelse',
    tidskonk: 'Tidskonkurranse',
    fri: 'Fri (poeng)',
};

function getStageTypeDisplay(type) {
    return STAGE_TYPE_DISPLAY[type] || type;
}

// Discipline + skill-class ordering mirrors the official results list and the Rubic PDF
// (backend/lib/rubic-results-grouping.js DISCIPLINE_ORDER / CLASS_CODE_ORDER) so the shared
// recap groups identically to what shooters see on the Resultater page.
const WEAPON_ORDER = ['fin', 'grov', 'mil', 'rev', 'sp.pistol', 'sp.rev', 'mag1', 'mag2', 'milrp', 'revrp', 'luft'];
const SKILL_CLASS_ORDER = ['A', 'B', 'C', 'D', 'U14', 'U16', 'U18', 'U21', 'Å', 'Å-2', 'Ingen klasse'];

// Single-class disciplines (SP/Magnum + Rødpunkt) have no A–D split, so their championship
// headings omit the skill-class suffix — the discipline name already identifies the class.
const SINGLE_CLASS_WEAPONS = ['sp.pistol', 'sp.rev', 'mag1', 'mag2', 'milrp', 'revrp'];

// Championship age/gender buckets, ordered + labelled as on the Resultater page (resultsTable.js).
const AGE_ORDER = { open: 0, u: 1, jr: 2, jk: 3, jm: 4, k: 5, m: 6, v55: 7, v65: 8, v73: 9 };
const AGE_LABEL = { u: 'U', jr: 'Jr', jk: 'Jk', jm: 'Jm', k: 'K', m: 'M', v55: 'V55', v65: 'V65', v73: 'V73' };

// Championship podium labels (top 3) — matches the Rubic PDF (MEDALS in rubic-results-grouping.js).
const CHAMP_MEDALS = ['Gull', 'Sølv', 'Bronse'];

function weaponRank(weapon) {
    const i = WEAPON_ORDER.indexOf(weapon);
    return i === -1 ? WEAPON_ORDER.length : i;
}

function skillClassRank(skillClass) {
    if (!skillClass) return -1; // null/'' (e.g. Norgesfelt) sorts first within a discipline
    const i = SKILL_CLASS_ORDER.indexOf(skillClass);
    return i === -1 ? SKILL_CLASS_ORDER.length : i;
}

function ageRank(age) {
    return age in AGE_ORDER ? AGE_ORDER[age] : 99;
}

function ageLabel(age) {
    return AGE_LABEL[age] || '';
}

function weaponOf(group) {
    return group.weaponClass || group.className;
}

function weaponDisplayOf(group) {
    return group.weaponDisplay || resolveWeaponDisplay(weaponOf(group));
}

function formatNorwegianDate(value) {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${d.getFullYear()}`;
}

function pickLocation(convention) {
    const candidates = [convention.place, convention.address, convention.venue];
    for (let i = 0; i < candidates.length; i += 1) {
        const v = candidates[i];
        if (typeof v === 'string' && v.trim().length > 0) return v.trim();
    }
    return '';
}

function countShotsFired(conventionResults) {
    if (!Array.isArray(conventionResults)) return 0;
    return conventionResults.reduce((acc, result) => {
        const participantClass = result.class || 'Ingen klasse';
        const shotsPerParticipant = ['sp.rev', 'sp.pistol'].includes(participantClass) ? 5 : 6;
        return acc + shotsPerParticipant;
    }, 0);
}

// Medals/marks are counted from the open buckets only — the championship (KM/DM) and age
// buckets are duplicates of the same shooters, so counting them too would inflate the totals.
function countMedals(groups) {
    const counts = { GM: 0, SM: 0, BM: 0, G: 0, S: 0, B: 0 };
    (groups || []).forEach(group => {
        const { stageType, className, participants = [] } = group;
        participants.forEach(p => {
            const mark = getOfficialMark(p.totalScore, className, stageType);
            if (mark && counts[mark.label] !== undefined) {
                counts[mark.label] += 1;
            }
        });
    });
    return counts;
}

function formatTopList(participants, isTimeCompetition) {
    const top = participants.slice(0, 3);
    if (top.length === 0) return '_Ingen resultater_';
    return top.map((p, idx) => {
        const place = idx + 1;
        const clubPart = p.sportsClub ? ` (${p.sportsClub})` : '';
        const scorePart = isTimeCompetition ? `${p.totalScore}s` : `${p.totalScore}`;
        return `${place}. **${p.name}**${clubPart} — ${scorePart}`;
    }).join('\n');
}

// Championship podium: top 3 labelled Gull / Sølv / Bronse (Rubic PDF style).
function formatChampionshipTopList(participants, isTimeCompetition) {
    const top = participants.slice(0, 3);
    if (top.length === 0) return '_Ingen resultater_';
    return top.map((p, idx) => {
        const medal = CHAMP_MEDALS[idx] || `${idx + 1}.`;
        const clubPart = p.sportsClub ? ` (${p.sportsClub})` : '';
        const scorePart = isTimeCompetition ? `${p.totalScore}s` : `${p.totalScore}`;
        return `${medal} — **${p.name}**${clubPart} — ${scorePart}`;
    }).join('\n');
}

function formatStageBreakdown(groups) {
    const byStageType = {};
    (groups || []).forEach(group => {
        const { stageType, participants = [], isTimeCompetition } = group;
        if (!byStageType[stageType]) {
            byStageType[stageType] = {
                isTimeCompetition,
                scores: [],
            };
        }
        participants.forEach(p => {
            if (typeof p.totalScore === 'number' && p.totalScore > 0) {
                byStageType[stageType].scores.push(p.totalScore);
            }
        });
    });

    const lines = [];
    Object.keys(byStageType).forEach(stageType => {
        const { isTimeCompetition, scores } = byStageType[stageType];
        if (scores.length === 0) return;
        const highest = isTimeCompetition ? Math.min(...scores) : Math.max(...scores);
        const avg = (scores.reduce((a, b) => a + b, 0) / scores.length);
        const avgFormatted = isTimeCompetition ? avg.toFixed(2) : Math.round(avg);
        const highestLabel = isTimeCompetition ? 'beste tid' : 'høyeste';
        const highestValue = isTimeCompetition ? `${highest}s` : highest;
        lines.push(`- **${getStageTypeDisplay(stageType)}** — ${highestLabel}: ${highestValue}, snitt: ${avgFormatted}`);
    });
    if (lines.length === 0) return '_Ingen resultater registrert._';
    return lines.join('\n');
}

// Open all-comers summary: top 3 per discipline + skill class, ordered like the results list.
function buildOpenTopBlock(openGroups) {
    if (openGroups.length === 0) {
        return '## Topp 3 per klasse\n\n_Ingen resultater registrert._';
    }
    // Prefix the discipline heading with the stage type only when the convention mixes stage
    // types (e.g. NAIS + Hurtig). A single-type stevne (all Felt) needs no prefix because the
    // weapon label already identifies the row — matching the Rubic PDF's "Finfelt :: Klasse A".
    const showStagePrefix = new Set(openGroups.map(g => g.stageType)).size > 1;
    const groupBlocks = openGroups.map(group => {
        const stagePrefix = showStagePrefix ? `${getStageTypeDisplay(group.stageType)} ` : '';
        const classSuffix = group.skillClass ? ` — Klasse ${group.skillClass}` : '';
        const heading = `### ${stagePrefix}${weaponDisplayOf(group)}${classSuffix}`;
        const list = formatTopList(group.participants, group.isTimeCompetition);
        return `${heading}\n\n${list}`;
    });
    return `## Topp 3 per klasse\n\n${groupBlocks.join('\n\n')}`;
}

// Championship (KM) summary: podium per championship discipline / age class, Rubic-PDF style.
function buildChampionshipBlock(kmGroups) {
    const groupBlocks = kmGroups.map(group => {
        const ageSuffix = group.ageGroup && group.ageGroup !== 'open' ? ` · ${ageLabel(group.ageGroup)}` : '';
        const classSuffix = (!SINGLE_CLASS_WEAPONS.includes(weaponOf(group)) && group.skillClass)
            ? ` — Klasse ${group.skillClass}`
            : '';
        const heading = `### KM: ${weaponDisplayOf(group)}${ageSuffix}${classSuffix}`;
        const list = formatChampionshipTopList(group.participants, group.isTimeCompetition);
        return `${heading}\n\n${list}`;
    });
    return `## KM-resultater\n\n${groupBlocks.join('\n\n')}`;
}

// yr/met.no symbolCode (base, without _day/_night suffix) → Norwegian label.
// Unknown codes simply omit the "Forhold" line — never guess a description.
const WEATHER_SYMBOL_LABELS = {
    clearsky: 'Klarvær',
    fair: 'Lettskyet',
    partlycloudy: 'Delvis skyet',
    cloudy: 'Overskyet',
    lightrain: 'Lett regn',
    rain: 'Regn',
    heavyrain: 'Kraftig regn',
    lightrainshowers: 'Lette regnbyger',
    rainshowers: 'Regnbyger',
    heavyrainshowers: 'Kraftige regnbyger',
    lightsnow: 'Lett snø',
    snow: 'Snø',
    heavysnow: 'Kraftig snøfall',
    snowshowers: 'Snøbyger',
    sleet: 'Sludd',
    sleetshowers: 'Sluddbyger',
    fog: 'Tåke',
};

function formatTempRange(min, max) {
    const lo = Math.round(min);
    const hi = Math.round(max);
    return lo === hi ? `${hi}°` : `${lo}° til ${hi}°`;
}

function formatWindRange(min, max) {
    const lo = Math.round(min);
    const hi = Math.round(max);
    return lo === hi ? `${hi} m/s` : `${lo} til ${hi} m/s`;
}

// Markdown "## Vær" block from the GET /api/yr/convention-summary payload
// (result.data: minTemp/maxTemp, minWind/maxWind, totalPrecipitation,
// mostCommonSymbol). Returns null when nothing usable is present so the
// section filter drops it.
export function buildWeatherBlock(weatherSummary) {
    if (!weatherSummary) {
        return null;
    }
    const { minTemp, maxTemp, minWind, maxWind, totalPrecipitation, mostCommonSymbol } = weatherSummary;
    const lines = [];
    if (minTemp != null && maxTemp != null) {
        lines.push(`- Temperatur: **${formatTempRange(minTemp, maxTemp)}**`);
    }
    if (minWind != null && maxWind != null) {
        lines.push(`- Vind: **${formatWindRange(minWind, maxWind)}**`);
    }
    if (typeof totalPrecipitation === 'number' && totalPrecipitation > 0) {
        lines.push(`- Nedbør: **${totalPrecipitation.toFixed(1)} mm**`);
    }
    const symbolBase = String(mostCommonSymbol || '').replace(/_(day|night|polartwilight)$/, '');
    if (WEATHER_SYMBOL_LABELS[symbolBase]) {
        lines.push(`- Forhold: **${WEATHER_SYMBOL_LABELS[symbolBase]}**`);
    }
    if (lines.length === 0) {
        return null;
    }
    return ['## Vær', ...lines].join('\n');
}

export function buildConventionRecap({
    convention,
    conventionResults,
    participants,
    classMap = [],
    disciplineMap = [],
    conventionResultStore,
    weatherSummary = null,
}) {
    if (!convention || !convention.id) {
        throw new Error('buildConventionRecap requires a convention with an id');
    }
    if (!conventionResultStore || typeof conventionResultStore.getResultsListForAllClasses !== 'function') {
        throw new Error('buildConventionRecap requires conventionResultStore with getResultsListForAllClasses');
    }

    const safeResults = Array.isArray(conventionResults) ? conventionResults : [];
    const safeParticipants = Array.isArray(participants) ? participants : [];

    // Same call the Resultater page makes (resultsTable.js) — classMap/disciplineMap are
    // REQUIRED for resolveSkillClass to bucket field disciplines into A/B/C/D. Omitting them
    // collapses every field shooter into a single "Ingen klasse" group.
    const resultList = conventionResultStore.getResultsListForAllClasses(
        convention,
        safeResults,
        safeParticipants,
        classMap,
        disciplineMap,
    );

    // getResultsListForAllClasses returns BOTH the open all-comers buckets AND duplicate
    // KM/DM championship + age buckets keyed by championship/ageGroup. Split them apart so the
    // recap never lists the same shooter twice.
    const groups = Object.keys(resultList)
        .map(key => resultList[key])
        .filter(group => group && (group.participants || []).length > 0);

    const openGroups = groups
        .filter(group => !group.championship)
        .sort((a, b) => {
            const w = weaponRank(weaponOf(a)) - weaponRank(weaponOf(b));
            if (w !== 0) return w;
            const s = skillClassRank(a.skillClass) - skillClassRank(b.skillClass);
            if (s !== 0) return s;
            return getStageTypeDisplay(a.stageType).localeCompare(getStageTypeDisplay(b.stageType));
        });

    const kmGroups = groups
        .filter(group => group.championship === 'KM')
        .sort((a, b) => {
            const w = weaponRank(weaponOf(a)) - weaponRank(weaponOf(b));
            if (w !== 0) return w;
            const ag = ageRank(a.ageGroup) - ageRank(b.ageGroup);
            if (ag !== 0) return ag;
            return skillClassRank(a.skillClass) - skillClassRank(b.skillClass);
        });

    const hasKM = kmGroups.length > 0;

    const participantCount = Array.isArray(convention.participants) ? convention.participants.length : safeParticipants.length;
    const stagesCount = Array.isArray(convention.stages) ? convention.stages.length : 0;
    const shotsFired = countShotsFired(safeResults);
    const location = pickLocation(convention);
    const dateStr = formatNorwegianDate(convention.startDate);

    const title = `Oppsummering: ${convention.title || 'Stevne'}`;

    let summary = `${participantCount} deltakere gjennomførte ${stagesCount} lag`;
    if (shotsFired > 0) {
        summary += ` og avfyrte totalt ${shotsFired} skudd`;
    }
    summary += '.';

    const participationBlock = [
        '## Deltakelse',
        dateStr ? `- Dato: **${dateStr}**` : null,
        location ? `- Sted: **${location}**` : null,
        `- Deltakere: **${participantCount}**`,
        `- Antall lag: **${stagesCount}**`,
        shotsFired > 0 ? `- Skudd avfyrt: **${shotsFired}**` : null,
    ].filter(Boolean).join('\n');

    const footer = `[Se alle resultater](/index#/convention/${convention.id})`;

    const weatherBlock = buildWeatherBlock(weatherSummary);

    // When the stevne has a KM, the recap is the championship podium only — everything else
    // (open classes, marks, lag) is linked, not inlined.
    let sections;
    if (hasKM) {
        sections = [
            summary,
            participationBlock,
            weatherBlock,
            buildChampionshipBlock(kmGroups),
            footer,
        ];
    } else {
        const medals = countMedals(openGroups);
        const medalLines = ['GM', 'SM', 'BM', 'G', 'S', 'B']
            .filter(label => medals[label] > 0)
            .map(label => `- **${label}**: ${medals[label]}`);
        const medalsBlock = medalLines.length > 0
            ? `## Merker og medaljer\n\n${medalLines.join('\n')}`
            : '## Merker og medaljer\n\n_Ingen merker oppnådd._';
        const stageBlock = `## Lagoversikt\n\n${formatStageBreakdown(openGroups)}`;
        sections = [
            summary,
            participationBlock,
            weatherBlock,
            buildOpenTopBlock(openGroups),
            medalsBlock,
            stageBlock,
            footer,
        ];
    }

    const ingress = sections.filter(section => section && section.length > 0).join('\n\n');

    // facts: the recap numbers, exposed so the caller can ask the backend for
    // the recap illustration (/api/ai/convention-recap-image) with the same
    // counts the text shows.
    return {
        title,
        ingress,
        body: '',
        facts: { participantCount, stagesCount, shotsFired },
    };
}

export default buildConventionRecap;
